import { Injectable, NgZone, Renderer2, RendererFactory2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Constant } from '../constant/constant';
import { EcryptServiceService } from './ecrypt-service.service';

@Injectable({
  providedIn: 'root'
})
export class TrackClickDirectiveService {
  private static readonly AUTH_POLL_INTERVAL_MS = 1000;
  private static readonly DEDUPE_WINDOW_MS = 800;
  private static readonly MAX_TEXT_LENGTH = 60;


  private apiUrl = environment.serverAPI9 + '/api/uploadUserActivity';  // Spring Boot endpoint
  private sessionId: string;
  private renderer: Renderer2;
  private listenerTeardowns: Array<() => void> = [];
  private trackingInitialized = false;
  private listenersAttached = false;
  private recentEvents = new Map<string, number>();
  private authPollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private http: HttpClient,
    private ecryptService: EcryptServiceService,
    private router: Router,
    private rendererFactory: RendererFactory2,
    private ngZone: NgZone
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.sessionId = this.generateSessionId();
    this.trackRouteChanges();
  }

  initializeTracking(): void {
    if (this.trackingInitialized) {
      return;
    }

    this.trackingInitialized = true;

    if (this.hasAuthenticatedSession()) {
      this.attachTrackingListeners();
      return;
    }

    this.waitForAuthenticatedSession();
  }

  private attachTrackingListeners(): void {
    if (this.listenersAttached) {
      return;
    }

    this.listenersAttached = true;
    this.clearAuthPollTimer();
    this.logPageView(this.getCurrentPageUrl());

    this.ngZone.runOutsideAngular(() => {
      this.listenerTeardowns.push(
        this.renderer.listen('document', 'click', (event: MouseEvent) => {
          const target = event.target instanceof HTMLElement ? event.target : null;
          const element = this.getTrackableElement(target);
          const elementId = this.resolveElementId(element);

          if (elementId) {
            this.logAction('CLICK', elementId, this.buildElementMetadata(element));
          }
        })
      );

      this.listenerTeardowns.push(
        this.renderer.listen('document', 'change', (event: Event) => {
          const target = event.target instanceof HTMLElement ? event.target : null;
          const element = this.getFormElement(target);

          if (!element) {
            return;
          }

          const elementId = this.resolveElementId(element);
          if (elementId) {
            this.logAction('CHANGE', elementId, this.buildFormMetadata(element));
          }
        })
      );

      this.listenerTeardowns.push(
        this.renderer.listen('document', 'submit', (event: Event) => {
          const target = event.target instanceof HTMLFormElement ? event.target : null;
          const elementId = this.resolveElementId(target);

          if (elementId) {
            this.logAction('SUBMIT', elementId, this.buildFormSubmitMetadata(target));
          }
        })
      );

      this.listenerTeardowns.push(
        this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
          if (event.key !== 'Enter') {
            return;
          }

          const target = event.target instanceof HTMLElement ? event.target : null;
          const element = this.getTrackableElement(target);
          const elementId = this.resolveElementId(element);

          if (elementId) {
            this.logAction('KEYBOARD_ENTER', elementId, this.buildElementMetadata(element));
          }
        })
      );
    });
  }

  private waitForAuthenticatedSession(): void {
    if (this.authPollTimer) {
      return;
    }

    this.authPollTimer = setInterval(() => {
      if (!this.hasAuthenticatedSession()) {
        return;
      }

      this.attachTrackingListeners();
    }, TrackClickDirectiveService.AUTH_POLL_INTERVAL_MS);
  }

  private generateSessionId(): string {
    return 'SID-' + Math.random().toString(36).substring(2) + Date.now();
  }

  private trackRouteChanges(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      try {
        const allowedPrefixes = ['/subscription/checkout', '/subscription/manage', '/subscription/plan-selection', '/new', '/renew', '/renewal'];
        const target = event.urlAfterRedirects || '';

        let isExpired = false;
        try {
          const raw = localStorage.getItem('trialExpiryData');
          if (raw) {
            const decrypted = this.ecryptService.decryptionAES(raw);
            const cache = JSON.parse(decrypted);
            if (cache && cache.expiryDate) {
              const expiry = new Date(cache.expiryDate);
              isExpired = expiry.getTime() < Date.now();
            }

            if (raw != null) {
              const isAllowed = allowedPrefixes.some(p => target.startsWith(p));
              if (isExpired && !isAllowed) {
                try { this.router.navigate(['/subscription/checkout']); } catch (navErr) { /* swallow navigation errors */ }
                return;
              }
            }
          }
        } catch (e) {
          isExpired = false;
        }




        this.logPageView(target);
      } catch (err) {
        try { this.logPageView(event.urlAfterRedirects); } catch (e) { }
      }
    });
  }

  private getTrackableElement(element: HTMLElement | null): HTMLElement | null {
    if (!element) {
      return null;
    }

    return (
      element.closest('[calsoftTrackClick], [trackGlobal]') ||
      element.closest('button, a, [role="button"], [mat-button], [mat-raised-button], [mat-flat-button], [mat-stroked-button], [mat-icon-button], [mat-menu-item], input[type="button"], input[type="submit"]') ||
      element
    );
  }

  private getFormElement(element: HTMLElement | null): HTMLElement | null {
    if (!element) {
      return null;
    }

    return element.closest('input, textarea, select, [contenteditable="true"]');
  }

  private resolveElementId(element: HTMLElement | null): string | null {
    if (!element) {
      return null;
    }

    const explicit = element.getAttribute('calsoftTrackClick') || element.getAttribute('trackGlobal');
    if (explicit && explicit.trim().length) {
      return explicit.trim();
    }

    const content = element.innerText ? element.innerText.trim().substring(0, 50) : '';
    const resolved =
      element.id ||
      element.getAttribute('name') ||
      element.getAttribute('formcontrolname') ||
      element.getAttribute('placeholder') ||
      element.getAttribute('data-track') ||
      element.getAttribute('aria-label') ||
      element.getAttribute('title') ||
      content ||
      element.tagName;

    return resolved ? String(resolved).trim() : null;
  }

  logPageView(url: string): void {
    this.logAction('PAGE_VIEW', null, { pageUrl: url });
  }

  logClick(elementId: string, extraData?: any): void {
    this.logAction('CLICK', elementId, extraData);
  }

  logAction(actionType: string, elementId: string | null, extraData?: Record<string, unknown> | null): void {
    const basicAuthHeaderString = this.getAuthToken();
    if (!basicAuthHeaderString) {
      return;
    }

    const pageUrl = this.getCurrentPageUrl();
    const dedupeKey = `${actionType}|${pageUrl}|${elementId ?? 'page'}|${JSON.stringify(extraData ?? {})}`;
    if (this.isDuplicateEvent(dedupeKey)) {
      return;
    }

    const payload = {
      userId: 123,
      sessionId: this.sessionId,
      actionType,
      pageUrl,
      elementId,
      extraData: extraData ? JSON.stringify(extraData) : null
    };

    this.http.post(this.apiUrl, payload).subscribe({
      next: () => { },
      error: () => { }
    });
  }

  private getCurrentPageUrl(): string {
    return window.location.pathname + window.location.search;
  }

  private hasAuthenticatedSession(): boolean {
    return Boolean(this.getAuthToken());
  }

  private getAuthToken(): string {
    if (Constant.DEVICE_TYPE === 'MOBILE') {
      return localStorage.getItem(Constant.X_XSRF_TOKEN) || '';
    }

    return this.getCookie(Constant.X_XSRF_TOKEN);
  }

  private getCookie(cookieName: string): string {
    const prefix = `${cookieName}=`;

    for (const cookieChunk of document.cookie.split(';')) {
      const cookie = cookieChunk.trim();
      if (cookie.startsWith(prefix)) {
        return cookie.substring(prefix.length);
      }
    }

    return '';
  }

  private clearAuthPollTimer(): void {
    if (!this.authPollTimer) {
      return;
    }

    clearInterval(this.authPollTimer);
    this.authPollTimer = null;
  }

  private isDuplicateEvent(eventKey: string): boolean {
    const now = Date.now();

    for (const [key, timestamp] of this.recentEvents.entries()) {
      if (now - timestamp > TrackClickDirectiveService.DEDUPE_WINDOW_MS) {
        this.recentEvents.delete(key);
      }
    }

    const lastSeen = this.recentEvents.get(eventKey);
    if (lastSeen && now - lastSeen < TrackClickDirectiveService.DEDUPE_WINDOW_MS) {
      return true;
    }

    this.recentEvents.set(eventKey, now);
    return false;
  }

  private buildElementMetadata(element: HTMLElement | null): Record<string, unknown> | null {
    if (!element) {
      return null;
    }

    return {
      tagName: element.tagName,
      role: element.getAttribute('role'),
      type: element.getAttribute('type'),
      text: this.getTrimmedText(element.innerText),
      guide: element.getAttribute('guide')
    };
  }

  private buildFormMetadata(element: HTMLElement): Record<string, unknown> | null {
    const htmlElement = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const type = (htmlElement.getAttribute('type') || htmlElement.tagName).toLowerCase();

    return {
      tagName: htmlElement.tagName,
      type,
      value: this.getSafeFieldValue(htmlElement),
      checked: 'checked' in htmlElement ? htmlElement.checked : undefined,
      formId: htmlElement.form?.id || htmlElement.form?.getAttribute('name') || null
    };
  }

  private buildFormSubmitMetadata(form: HTMLFormElement | null): Record<string, unknown> | null {
    if (!form) {
      return null;
    }

    return {
      method: form.getAttribute('method') || 'GET',
      action: form.getAttribute('action') || null,
      fieldCount: form.elements.length
    };
  }

  private getSafeFieldValue(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string | number | boolean | null {
    const type = (element.getAttribute('type') || '').toLowerCase();

    if (type === 'password' || type === 'hidden' || type === 'file') {
      return null;
    }

    if (element instanceof HTMLInputElement && (type === 'checkbox' || type === 'radio')) {
      return element.checked;
    }

    if (element instanceof HTMLSelectElement) {
      const selectedText = element.selectedOptions.length ? element.selectedOptions[0].text : element.value;
      return this.getTrimmedText(selectedText);
    }

    return this.getTrimmedText(element.value);
  }

  private getTrimmedText(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const trimmed = value.replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      return null;
    }

    return trimmed.substring(0, TrackClickDirectiveService.MAX_TEXT_LENGTH);
  }
}
